import { createNamedErrorClass, throttle } from '@common/neo.tax';

import { LocalStorageKey } from '@neo.tax/shared/enums';
import { clearFirebase } from '@neo.tax/shared/third-parties/firebase';

import { env } from './env';

export const NullUserInactivityError = createNamedErrorClass('NullUserInactivityError');
export const InvalidUserInactivityError = createNamedErrorClass('InvalidUserInactivityError');
export const InvalidMaxUserInactivityError = createNamedErrorClass('InvalidMaxUserInactivityError');

const maxUserInactivityMs = env.REACT_APP_MAX_USER_INACTIVITY_SECONDS * 1000;
const oneMinuteMs = 60_000;
// IF the max user inactivity is less than one minute THEN we want to check for user inactivity just as frequently
// OTHERWISE we want to check for user inactivity at least every minute; more than this is unnecessary
const delayMs = maxUserInactivityMs < oneMinuteMs ? maxUserInactivityMs : oneMinuteMs;

export enum SyntheticEvent {
  UPLOAD_CHUNK_ATTEMPT = 'UPLOAD_CHUNK_ATTEMPT', // used to keep user active during resumable uploads
}

const trackedEventTypes = ['mousemove', 'keydown', 'touchstart', SyntheticEvent.UPLOAD_CHUNK_ATTEMPT] as const;

let userActivityInterval: NodeJS.Timeout | null = null;

const addEventListenersToTrackUserActivity = (): void => {
  trackedEventTypes.forEach((eventType: string) => {
    window.addEventListener(eventType, updateUserActivityThrottled);
  });
};

const removeEventListenersToTrackUserActivity = (): void => {
  trackedEventTypes.forEach((eventType: string) => {
    window.removeEventListener(eventType, updateUserActivityThrottled);
  });
};

export const calculateMillisecondsSinceUserLastActive = (): number => {
  const lastTimeUserActiveRaw = window.localStorage.getItem(LocalStorageKey.SESSION_USER_ACTIVITY_TIMESTAMP_KEY);
  if (lastTimeUserActiveRaw === null) {
    throw new NullUserInactivityError('No record of when user was last active found in local storage');
  }

  const lastTimeUserActiveMs = Number.parseInt(lastTimeUserActiveRaw, 10);
  if (Number.isNaN(lastTimeUserActiveMs)) {
    throw new InvalidUserInactivityError('Invalid value for when user was last active found in local storage');
  }

  return Date.now() - lastTimeUserActiveMs;
};

export const isUserInactive = (): boolean => {
  const msSinceUserLastActive = calculateMillisecondsSinceUserLastActive();

  return msSinceUserLastActive > maxUserInactivityMs;
};

export const startTrackingUserInactivity = (): void => {
  // Make sure we clear any existing interval; should only be one
  stopTrackingUserInactivity();
  addEventListenersToTrackUserActivity();
  userActivityInterval = setInterval(() => {
    try {
      if (isUserInactive()) {
        clearFirebase();
      }
    } catch (e) {
      if (e instanceof NullUserInactivityError) {
        updateUserActivity();
        return;
      }
      throw e;
    }
  }, delayMs);
};

export const stopTrackingUserInactivity = (): void => {
  if (!userActivityInterval) {
    return;
  }
  clearInterval(userActivityInterval);
  removeEventListenersToTrackUserActivity();
};

export const updateUserActivity = (): void => {
  window.localStorage.setItem(LocalStorageKey.SESSION_USER_ACTIVITY_TIMESTAMP_KEY, Date.now().toString());
};

export const updateUserActivityThrottled = throttle((_event: Event) => {
  updateUserActivity();
}, delayMs);
